import React from 'react';
import IV from '../InputValidation/InputValidation';
import ToolTip from '@appComponents/ToolTip/ToolTip';
import QuestionMark from '@appComponents/QuestionMark/QuestionMark';
import { IFundingRowProps, TFundingRowState, TType, ESpecialization } from './IFunding';
import { displayURL, formatDate, numberFormatter, shortenText } from '@features/utils/functions';

import * as appStyles from '../../AppStyle.module.scss';
import * as styles from './FundingStyle.module.scss';

const types: TType = {
  seed: 'Seed Funding',
  angel: 'Angel Funding',
  vc: 'VC Funding',
  series_a: 'Series A Funding',
  series_b: 'Series B Funding',
  ico: 'ICO',
  other: 'Other',
};

export default class Row extends React.Component<IFundingRowProps, TFundingRowState> {
  state = {
    editing: this.props.data.date === '' && this.props.data.name === '',
    hideError: {
      date: true,
      name: true,
      website: true,
      money_raised: true,
    },
  };

  inputFocus = (e: React.BaseSyntheticEvent) => {
    this.setState({
      hideError: {
        ...this.state.hideError,
        [e.target.name]: false,
      },
    });
  }

  deleteRow = () => {
    this.props.deleteRow(this.props.data.id);
  }

  onChange = (e: React.ChangeEvent) => {
    this.props.changeValue(this.props.data.id, e);
  }

  toggleEditing = () => {
    this.setState({ editing: !this.state.editing });
  }

  renderDescription = () => {
    const { data } = this.props;
    return (
      <IV type="noEmpty" dispatch={this.props.dispatch}>
        {data.website ? (
          <a
            className={!this.props.editMode ? styles.externalLink : undefined}
            href={displayURL(data.website)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {shortenText(data.name, 255)}
          </a>
        ) : data.slug ? (
          <a href={`/pitches/${data.slug}`}>
            {shortenText(data.name, 255)}
            {!this.props.editMode && <span className={[styles.flag, 'fa fa-flag'].join(' ')} />}
          </a>
        ) : (
          <span title="asd">
            {shortenText(data.name, 255)}
          </span>
        )}
      </IV>
    );
  }

  renderSelect = () => {
    return (
      <select name="funding_type" value={this.props.data.funding_type} onChange={this.onChange}>
        {Object.keys(types).map(el => (
          <option key={el} value={el}>{types[el]}</option>
        ))}
      </select>
    );
  }

  renderTooltip = () => {
    switch (this.props.data.specialization) {
      case ESpecialization.ConvertibleNotePitch:
        return (
          <ToolTip
            text="The capital raised in this round was a convertible note instrument,
            which is debt and did not have a direct share price at the point of investment"
          >
            <QuestionMark />
          </ToolTip>
        );
      case ESpecialization.TokenPitch:
        return (
          <ToolTip
            text="The capital raised in this round was a token investment and
            did not have a direct share price at the point of investment"
          >
            <QuestionMark />
          </ToolTip>
        );
      default:
        return <span className={styles.notKnown}>not known</span>;
    }
  }

  renderRow = () => {
    const { dispatch, data, editMode } = this.props;

    return (
      <tr className={[styles.row, editMode ? styles.rowEditmode : undefined].join(' ')} id={String(data.id)}>
        <td data-title="Round: ">
          <span>{this.props.round}</span>
        </td>
        <td data-title="Date: ">
          <IV type="yymmdd" dispatch={dispatch}>
            <span>{formatDate(data.date)}</span>
          </IV>
        </td>
        <td data-title="Type: " className={styles.uppercase}>
          <span>{types[data.funding_type]}</span>
        </td>
        <td data-title="Description: ">
          {this.renderDescription()}
        </td>
        {editMode && this.props.data.website !== undefined
          ? <td data-title="URL: " className={styles.breakWords}>
              <IV type="UrlEmpty" dispatch={dispatch}><span>{this.props.data.website}</span></IV>
            </td>
          : editMode ? <td className={styles.mobileHide} /> : null
        }
        <td data-title="Share Price: ">
          <span>
            {data.share_price
              ? '$' + numberFormatter(data.share_price || '', 2)
              : this.renderTooltip()
            }
          </span>
        </td>
        <td data-title="Amount: ">
          <IV type="fundingMoney" dispatch={dispatch}>
            <span>{`$${numberFormatter(data.money_raised, 2)}`}</span>
          </IV>
        </td>
        {editMode && data.website !== undefined
          ? <td>
              <button className={[appStyles.editButton, styles.editButton].join(' ')} onClick={this.toggleEditing}/>
              <button className={appStyles.cancelButton} onClick={this.deleteRow} />
            </td>
          : editMode ? <td className={styles.mobileHide} /> : null
        }
      </tr>
    );
  }

  renderEditingRow = () => {
    const { dispatch, data, editMode } = this.props;
    const className = [styles.row, styles.rowEditing, editMode ? styles.rowEditmode : undefined].join(' ');
    return (
      <tr className={className} id={String(data.id)}>
        <td data-title="Round: ">
          <span>{this.props.round}</span>
        </td>
        <td>
          <IV type="yymmdd" hide={this.state.hideError.date} dispatch={dispatch}>
            <input
              value={data.date}
              onFocus={this.inputFocus}
              onChange={this.onChange}
              name="date"
              placeholder="YYYY-MM-DD"
            />
          </IV>
        </td>
        <td>
          {this.renderSelect()}
        </td>
        <td>
          <IV type="noEmpty" hide={this.state.hideError.name} dispatch={dispatch}>
            <input
              value={data.name}
              onFocus={this.inputFocus}
              onChange={this.onChange}
              name="name"
              placeholder="Description"
            />
          </IV>
        </td>
        {this.props.data.website !== undefined
          ? <td>
              <IV type="UrlEmpty" hide={this.state.hideError.website} dispatch={dispatch}>
                <input
                  value={data.website}
                  onFocus={this.inputFocus}
                  onChange={this.onChange}
                  name="website"
                  placeholder="Website URL"
                />
              </IV>
            </td>
          : <td>---</td>
        }
        <td>
          <input
            value={this.props.data.share_price || ''}
            onFocus={this.inputFocus}
            onChange={this.onChange}
            name="share_price"
            placeholder="Share Price"
            type="number"
            onWheel={event => event.currentTarget.blur()}
          />
        </td>
        <td>
          <IV type="fundingMoney" hide={this.state.hideError.money_raised} dispatch={dispatch}>
            <input
              value={this.props.data.money_raised}
              onFocus={this.inputFocus}
              onChange={this.onChange}
              name="money_raised"
              placeholder="Money raised"
              type="number"
              onWheel={event => event.currentTarget.blur()}
            />
          </IV>
        </td>
        <td>
          <button className={[appStyles.editButtonActive, styles.editButton].join(' ')} onClick={this.toggleEditing}/>
          <button className={appStyles.cancelButton} onClick={this.deleteRow} />
        </td>
      </tr>
    );
  }

  render() {
    return (
      this.state.editing ? this.renderEditingRow() : this.renderRow()
    );
  }
}

